import { cn } from '@/design-system/cn';
import { mix, type BrushKind } from './media';

/**
 * The drawing tools, drawn as the objects they are.
 *
 * A row of abstract glyphs asks a five-year-old to remember which square means
 * "crayon". A crayon does not. Each implement here is a real object with a
 * tip, a body and a label, and it carries the child's CURRENT COLOUR in its
 * barrel — so before making a single mark they can see what a stroke will look
 * like, and choosing a colour visibly changes the tools in their hand.
 *
 * The selected one slides out of the tray, the way you pull a pen from a pot.
 */

interface ImplementProps {
  kind: BrushKind;
  ink: string;
  className?: string;
}

const VIEW = '0 0 132 40';

export function Implement({ kind, ink, className }: ImplementProps) {
  const dark = mix(ink, '#000000', 0.3);
  const light = mix(ink, '#FFFFFF', 0.35);
  const common = { className: cn('h-full w-full', className), viewBox: VIEW, 'aria-hidden': true } as const;

  switch (kind) {
    case 'marker':
      return (
        <svg {...common}>
          <path d="M4 20 22 11v18Z" fill={ink} />
          <rect x="20" y="8" width="12" height="24" rx="3" fill={dark} />
          <rect x="30" y="6" width="78" height="28" rx="9" fill={ink} />
          <rect x="34" y="10" width="66" height="8" rx="4" fill={light} opacity="0.55" />
          <rect x="104" y="4" width="24" height="32" rx="10" fill={dark} />
          <Edge d="M4 20 22 11v18Z" />
          <Edge d="M30 6h68a10 10 0 0 1 10 10v8a10 10 0 0 1-10 10H30Z" />
        </svg>
      );
    case 'pencil':
      return (
        <svg {...common}>
          {/* A colouring pencil: the lead and the barrel are the child's
              colour, the shaved wood stays wood. */}
          <path d="M6 20 20 14v12Z" fill={dark} />
          <path d="M20 12 38 7v26l-18-5Z" fill="#F0DCC0" />
          <path d="M20 12 38 7v9l-18 2Z" fill="#FBEEDC" />
          <rect x="36" y="7" width="66" height="26" rx="3" fill={ink} />
          <rect x="36" y="11" width="66" height="7" rx="3" fill={light} opacity="0.7" />
          <rect x="100" y="7" width="12" height="26" fill="#C9D2DC" />
          <path d="M102 9v22M106 9v22M110 9v22" stroke="#8C99A8" strokeWidth="1.2" />
          <rect x="110" y="6" width="18" height="28" rx="8" fill="#F3B6C8" />
          <Edge d="M6 20 20 14v12Z" />
          <Edge d="M20 12 38 7v26l-18-5Z" />
          <Edge d="M36 7h66v26H36Z" />
        </svg>
      );
    case 'paint':
      return (
        <svg {...common}>
          <path
            d="M2 20c0-9 10-15 22-15 7 0 12 3 12 3v24s-5 3-12 3C12 35 2 29 2 20Z"
            fill={ink}
          />
          <path
            d="M8 12c7-3 15-3 22-1M8 28c7 3 15 3 22 1"
            stroke={dark}
            strokeWidth="1.8"
            fill="none"
            strokeLinecap="round"
          />
          <rect x="34" y="7" width="20" height="26" rx="4" fill="#C9D2DC" />
          <rect x="34" y="12" width="20" height="5" fill="#8C99A8" />
          <path d="M52 9h56a6 6 0 0 1 6 6v10a6 6 0 0 1-6 6H52Z" fill="#8C5A3A" />
          <rect x="56" y="13" width="50" height="5" rx="2.5" fill="#B07C55" />
          <Edge d="M2 20c0-9 10-15 22-15 7 0 12 3 12 3v24s-5 3-12 3C12 35 2 29 2 20Z" />
          <Edge d="M52 9h56a6 6 0 0 1 6 6v10a6 6 0 0 1-6 6H52Z" />
        </svg>
      );
    case 'watercolor':
      return (
        <svg {...common}>
          <path d="M16 8c6 7 9 11 9 15a9 9 0 0 1-18 0c0-4 3-8 9-15Z" fill={ink} opacity="0.75" />
          <path d="M13 22a4 4 0 0 0 4 4" stroke="#FFFFFF" strokeWidth="2" fill="none" strokeLinecap="round" />
          <rect x="28" y="9" width="76" height="22" rx="8" fill={light} />
          <rect x="28" y="9" width="42" height="22" rx="8" fill={ink} opacity="0.55" />
          <rect x="100" y="6" width="26" height="28" rx="6" fill={dark} />
          <path d="M106 12v16M112 12v16M118 12v16" stroke={light} strokeWidth="1.6" opacity="0.7" />
          <Edge d="M16 8c6 7 9 11 9 15a9 9 0 0 1-18 0c0-4 3-8 9-15Z" />
          <Edge d="M28 9h68a8 8 0 0 1 8 8v6a8 8 0 0 1-8 8H28Z" />
        </svg>
      );
    case 'crayon':
    case 'glitter':
      return (
        <svg {...common}>
          <path d="M5 20 24 9c3 3 3 19 0 22Z" fill={dark} />
          <rect x="22" y="7" width="86" height="26" rx="5" fill={ink} />
          <rect x="30" y="7" width="70" height="26" fill={light} opacity="0.85" />
          <path d="M34 7v26M40 7v26M94 7v26M100 7v26" stroke={dark} strokeWidth="1.4" opacity="0.5" />
          <rect x="104" y="7" width="22" height="26" rx="5" fill={ink} />
          {kind === 'glitter' ? (
            <g fill="#FFFFFF">
              <circle cx="48" cy="14" r="1.8" />
              <circle cx="62" cy="26" r="2.2" />
              <circle cx="76" cy="16" r="1.6" />
              <circle cx="88" cy="24" r="2" />
              <circle cx="56" cy="20" r="1.4" />
            </g>
          ) : null}
          <Edge d="M5 20 24 9c3 3 3 19 0 22Z" />
          <Edge d="M22 7h99a5 5 0 0 1 5 5v16a5 5 0 0 1-5 5H22Z" />
        </svg>
      );
    case 'spray':
      return (
        <svg {...common}>
          <g fill={ink} opacity="0.55">
            <circle cx="6" cy="12" r="2" />
            <circle cx="10" cy="24" r="1.6" />
            <circle cx="14" cy="16" r="2.4" />
            <circle cx="18" cy="28" r="1.4" />
          </g>
          <rect x="22" y="15" width="12" height="10" rx="2" fill="#8C99A8" />
          <rect x="32" y="10" width="10" height="20" rx="3" fill="#C9D2DC" />
          <rect x="40" y="5" width="70" height="30" rx="10" fill={ink} />
          <rect x="46" y="10" width="58" height="7" rx="3.5" fill={light} opacity="0.7" />
          <rect x="46" y="22" width="34" height="6" rx="3" fill={dark} opacity="0.6" />
          <Edge d="M40 5h60a10 10 0 0 1 10 10v10a10 10 0 0 1-10 10H40Z" />
        </svg>
      );
    case 'rainbow':
      return (
        <svg {...common}>
          <defs>
            <linearGradient id="imp-rainbow" x1="0" x2="1">
              <stop offset="0" stopColor="#E23359" />
              <stop offset="0.25" stopColor="#FFC94D" />
              <stop offset="0.5" stopColor="#56BA7D" />
              <stop offset="0.75" stopColor="#4A7BE0" />
              <stop offset="1" stopColor="#E233AD" />
            </linearGradient>
          </defs>
          <path d="M4 20 22 11v18Z" fill="url(#imp-rainbow)" />
          <rect x="20" y="8" width="12" height="24" rx="3" fill="#7C4FE8" />
          <rect x="30" y="6" width="78" height="28" rx="9" fill="url(#imp-rainbow)" />
          <rect x="104" y="4" width="24" height="32" rx="10" fill="#7C4FE8" />
          <Edge d="M4 20 22 11v18Z" />
          <Edge d="M30 6h68a10 10 0 0 1 10 10v8a10 10 0 0 1-10 10H30Z" />
        </svg>
      );
  }
}

/** The dark casing line that gives every object the same drawn edge. */
function Edge({ d }: { d: string }) {
  return <path d={d} fill="none" stroke="#2C1C38" strokeWidth="2.2" strokeLinejoin="round" />;
}

/** The eraser, drawn the same way so it belongs in the same hand. */
export function EraserObject({ className }: { className?: string }) {
  return (
    <svg className={cn('h-full w-full', className)} viewBox={VIEW} aria-hidden>
      <path d="M8 26 26 8a6 6 0 0 1 8 0l14 14a6 6 0 0 1 0 8L34 34H16Z" fill="#F3B6C8" />
      <path d="M34 34 48 22 34 8" fill="#E28FA8" />
      <rect x="52" y="9" width="60" height="22" rx="6" fill="#DCE4FB" />
      <rect x="52" y="9" width="60" height="8" rx="4" fill="#F2F6FF" />
      <path
        d="M8 26 26 8a6 6 0 0 1 8 0l14 14a6 6 0 0 1 0 8L34 34H16Z"
        fill="none"
        stroke="#2C1C38"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <path d="M52 9h54a6 6 0 0 1 6 6v10a6 6 0 0 1-6 6H52Z" fill="none" stroke="#2C1C38" strokeWidth="2.2" />
    </svg>
  );
}

/** The paint pot for the fill tool — a colour tipping into a shape. */
export function FillObject({ ink, className }: { ink: string; className?: string }) {
  const dark = mix(ink, '#000000', 0.28);
  return (
    <svg className={cn('h-full w-full', className)} viewBox={VIEW} aria-hidden>
      <path d="M10 24c0-6 8-10 18-10s18 4 18 10-8 10-18 10-18-4-18-10Z" fill={ink} opacity="0.85" />
      <path d="M28 6c5 7 8 10 8 13a8 8 0 0 1-16 0c0-3 3-6 8-13Z" fill={ink} />
      <rect x="54" y="8" width="58" height="26" rx="6" fill={dark} />
      <rect x="54" y="8" width="58" height="9" rx="4.5" fill={ink} />
      <path d="M60 4h46v6H60Z" fill="#C9D2DC" />
      <path
        d="M28 6c5 7 8 10 8 13a8 8 0 0 1-16 0c0-3 3-6 8-13Z"
        fill="none"
        stroke="#2C1C38"
        strokeWidth="2.2"
      />
      <path d="M54 8h52a6 6 0 0 1 6 6v14a6 6 0 0 1-6 6H54Z" fill="none" stroke="#2C1C38" strokeWidth="2.2" />
    </svg>
  );
}
